import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { toLocalInput, fromLocalInput, photoUrl } from "../../lib/customers";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { CustomerForm } from "../CustomerForm";

const KEYS = ["name", "father_name", "mother_name", "dob", "gender", "mobile", "alt_mobile", "email", "insta_id", "address", "village",
  "post_office", "panchayat", "block", "district", "state", "pin", "qualification", "customer_type", "enquiry_subject", "revisit_at",
  "call_back", "add_followup", "consent", "retention_until", "branch_id"];

const toForm = (c) => {
  const f = Object.fromEntries(KEYS.map((k) => [k, c?.[k] ?? (["call_back", "add_followup", "consent"].includes(k) ? false : "")]));
  f.revisit_at = toLocalInput(c?.revisit_at);
  if (c?.id) f.id = c.id;
  return f;
};

const DuplicateBox = ({ dup, onOpen, onForce, onRequest }) => (
  <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm space-y-2" data-testid="duplicate-alert">
    <div className="font-semibold text-amber-800">Duplicate Customer Alert</div>
    <div>Ye mobile pehle se registered hai: <b>{dup.name}</b> ({dup.customer_code}) · {dup.branch_name}</div>
    <div className="flex flex-wrap gap-2">
      {dup.accessible && <Button size="sm" variant="outline" onClick={onOpen} data-testid="dup-open-btn">Purana profile kholein</Button>}
      {dup.accessible && <Button size="sm" variant="outline" onClick={onForce} data-testid="dup-force-btn">Phir bhi naya banayein</Button>}
      {!dup.accessible && <Button size="sm" variant="outline" onClick={() => onRequest("link")} data-testid="dup-link-btn">Meri branch se link karne ki request</Button>}
      {!dup.accessible && <Button size="sm" variant="outline" onClick={() => onRequest("transfer")} data-testid="dup-transfer-btn">Transfer request</Button>}
    </div>
  </div>
);

export const CustomerEditor = ({ customer, onClose, onSaved, onOpenExisting }) => {
  const [form, setForm] = useState(() => toForm(customer));
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(customer ? photoUrl(customer) : null);
  const [saving, setSaving] = useState(false);
  const [dup, setDup] = useState(null);

  const save = async (force = false) => {
    if (!form.name.trim() || !form.mobile.trim()) { toast.error("Naam aur mobile zaroori hai"); return; }
    if (form.add_followup && !form.revisit_at) { toast.error("Follow-up ke liye 'Dubara kab aayega' bharein"); return; }
    setSaving(true);
    try {
      const body = { ...form, revisit_at: fromLocalInput(form.revisit_at), branch_id: form.branch_id || null, force };
      delete body.id;
      const r = form.id ? await api.put(`/customers/${form.id}`, body) : await api.post("/customers", body);
      if (photo) {
        const fd = new FormData(); fd.append("file", photo);
        await api.post(`/customers/${r.data.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Customer save ho gaya");
      onSaved(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.duplicate) setDup(d.duplicate);
      else toast.error(typeof d === "string" ? d : "Save failed");
    } finally { setSaving(false); }
  };

  const requestTransfer = async (type) => {
    try { await api.post(`/customers/${dup.id}/transfer-request`, { type }); toast.success("Request Admin/Central ko bhej di gayi"); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="customer-dialog">
        <DialogHeader><DialogTitle>{form.id ? "Customer Edit" : "Naya Customer"}</DialogTitle></DialogHeader>
        {dup && <DuplicateBox dup={dup} onOpen={() => onOpenExisting(dup.id)} onForce={() => save(true)} onRequest={requestTransfer} />}
        <CustomerForm form={form} setForm={setForm} preview={preview} onPhoto={(f) => { setPhoto(f); setPreview(URL.createObjectURL(f)); }} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="cust-cancel-btn">Cancel</Button>
          <Button onClick={() => save(false)} disabled={saving} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="cust-save-btn">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
